/**
 * PATH: packages/job-hub/src/jobs/qa/qaCrossDoorLinks.ts
 * FILE_ID: JOBHUB-QA-CROSS-DOOR-LINKS-001
 * PURPOSE: One place that builds the two cross-hub QA deep links and names the
 *          shared list contract both QA doors read.
 * WHY: QA is reachable from the job hub tab and from the standalone QA hub.
 *      Each door had its own URL knowledge, so a link could be built against
 *      the wrong identifier and silently open a different record.
 * LANDMINE: `/jobs/:id` routes on the job hub key off the Simpro job number
 *      (Inspection.job_id / Job.job_id), NOT the Job primary key that the
 *      inspection detail payload exposes as `job`. Passing the pk yields a
 *      job hub page for an unrelated job or a 404.
 */

/** Origin of the standalone QA hub SPA. */
export const QA_HUB_ORIGIN = 'https://qa.quotech.ai'

/** Origin of the job hub SPA that hosts the per-job QA tab. */
export const JOBS_HUB_ORIGIN = 'https://dashboard.quotech.ai'

/** Origins used by New Job chooser destinations. */
export const CRM_HUB_ORIGIN = 'https://crm.quotech.ai'
export const QUOTE_HUB_ORIGIN = 'https://quote.quotech.ai'

export function crmQuotesCreateJobUrl(returnTo?: string, search?: string): string {
  const params = new URLSearchParams({ intent: 'create-job' })
  if (returnTo) params.set('returnTo', returnTo)
  if (search) params.set('search', search)
  return `${CRM_HUB_ORIGIN}/quotes?${params.toString()}`
}

export function quoteUploadUrl(): string {
  return `${QUOTE_HUB_ORIGIN}/import`
}

/**
 * Endpoint both doors list inspections from. Kept here so a test can assert the
 * two doors read one collection rather than two divergent ones.
 */
export const QA_INSPECTIONS_ENDPOINT = '/api/qa/inspections/'

/** (inspectionId) → the QA hub record page for that same inspection. */
export function qaHubRecordUrl(inspectionId: string): string {
  return `${QA_HUB_ORIGIN}/records/${encodeURIComponent(inspectionId)}`
}

/**
 * (simproJobNumber) → the job hub page for that job with the QA tab selected.
 * `?tab=qa&qaMode=build` is the job hub's deep-link contract
 * (useJobHubPageState reads tab, useQaWorkspaceUrl reads qaMode).
 */
export function jobQaTabUrl(simproJobNumber: string | number, companySlug?: string | null): string {
  const jobPath = `/jobs/${encodeURIComponent(String(simproJobNumber))}?tab=qa&qaMode=build`
  if (companySlug) {
    return `${JOBS_HUB_ORIGIN}/company/${encodeURIComponent(companySlug)}${jobPath}`
  }
  return `${JOBS_HUB_ORIGIN}${jobPath}`
}

/**
 * (simproJobNumber) → the query both doors send to scope the inspection list to
 * one job. The backend resolves `job_id` against `job__job_id`, so this is the
 * Simpro number and not the Job pk.
 *
 * The caller's own type is preserved rather than coerced: the job hub tab holds
 * the number and the QA hub filter holds the text a user typed, and both
 * serialise to the identical `job_id=<n>` query string.
 */
export function qaInspectionsJobQuery<T extends string | number>(simproJobNumber: T): { job_id: T } {
  return { job_id: simproJobNumber }
}
